import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch, FaEdit, FaTrash, FaGripHorizontal, FaCircle, FaExclamationCircle, FaCog } from 'react-icons/fa';
import { 
  TextField, 
  IconButton,
  Box,
  InputAdornment,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Chip
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, MatrixGrid, MatrixCard, MatrixCardContent, MatrixTitle, MatrixDetailsGrid, ActionButtons } from '../SkillsMatrixTemplates/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';
import { skillsApi } from '../../../services/api';
import RolesSelection from '../../ProjectsManagement/ProjectForm/components/Employees/components/EmployeeTransfer/components/RolesSelection';
import { MATRIX_TEMPLATE_STATUS, getStatusColor, getStatusTextColor } from '../../../constants/matrixTemplateStatus';
import { COLORS } from '../../../constants/radar';
import { MATRIX_TEMPLATE_TYPES, getTemplateTypeColor, getTemplateTypeText } from '../../../constants/matrixTemplateTypes';
import { ROLE_TYPES, getRoleChipStyle } from '../../../constants/roleTypes';

const getSteps = (mode, showCopyTemplate) => {
  if (mode === 'add' && showCopyTemplate) {
    return ['Matrix Details', 'Position and Roles', 'Copy Template'];
  }
  return ['Matrix Details', 'Position and Roles'];
};

const SkillsMatrixList = ({
  // Configuration props
  title = 'Skills Matrix Templates',
  backButtonText = 'Back to Skills Matrix',
  backButtonPath = '/skills-matrix',
  fetchMatricesFunction = skillsApi.getAllSkillsMatrices,
  
  // Feature flags
  allowCRUD = true,
  allowAdd = true,
  allowEdit = true,
  allowDelete = true,
  allowManageVersions = true,
  showCopyTemplate = true,
  
  // Custom props
  sessionId = null,
  emptyStateTitle = null,
  emptyStateDescription = null,
  searchPlaceholder = "Search Matrix Templates",
  addButtonText = "Add Matrix Template",
  addButtonColor = '#2563eb',
  allowSelectVersions = false,
  noVersionsMessage = "No versions created yet",
  
  // Additional data to pass to API calls
  additionalApiParams = {}
}) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [matrices, setMatrices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'add',
    matrix: null,
    formData: { name: '', positionId: '', roleIds: [], templateToCopyId: null }
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, matrix: null });
  const [currentStep, setCurrentStep] = useState(1);

  // Role selection state
  const [selectedPositionForRoles, setSelectedPositionForRoles] = useState(null);
  const [selectedFunctionalRoles, setSelectedFunctionalRoles] = useState([]);
  const [selectedTechnicalRoles, setSelectedTechnicalRoles] = useState([]);
  const [selectedOrganizationalRoles, setSelectedOrganizationalRoles] = useState([]);

  const fetchMatrices = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await fetchMatricesFunction();
      setMatrices(data);
    } catch (err) {
      showAlert('Failed to load skills matrices.', 'error');
      console.error('Error fetching skills matrices');
    } finally {
      setIsLoading(false);
    }
  }, [showAlert, fetchMatricesFunction]);

  useEffect(() => {
    fetchMatrices();
  }, [fetchMatrices]);

  const handleModalOpen = (mode, matrix = null) => {
    if (!allowCRUD || (mode === 'add' && !allowAdd) || (mode === 'edit' && !allowEdit)) {
      return;
    }

    setModalState({
      isOpen: true,
      mode,
      matrix,
      formData: matrix ? {
        name: matrix.name,
        positionId: matrix.positionId,
        roleIds: matrix.matrixRoles?.map(mr => mr.roleId) || [],
        templateToCopyId: matrix.id
      } : { name: '', positionId: '', roleIds: [], templateToCopyId: null }
    });

    if (matrix) {
      setSelectedPositionForRoles({ id: matrix.positionId, label: matrix.position.label });
      const rolesByType = matrix.matrixRoles.reduce((acc, mr) => {
        if (mr.role.type === ROLE_TYPES.FUNCTIONAL) acc.functional.push(mr.roleId);
        if (mr.role.type === ROLE_TYPES.TECHNICAL) acc.technical.push(mr.roleId);
        if (mr.role.type === ROLE_TYPES.ORGANIZATIONAL) acc.organizational.push(mr.roleId);
        return acc;
      }, { functional: [], technical: [], organizational: [] });

      setSelectedFunctionalRoles(rolesByType.functional);
      setSelectedTechnicalRoles(rolesByType.technical);
      setSelectedOrganizationalRoles(rolesByType.organizational);
    } else {
      setSelectedPositionForRoles(null);
      setSelectedFunctionalRoles([]);
      setSelectedTechnicalRoles([]);
      setSelectedOrganizationalRoles([]);
    }
  };

  const handleModalClose = () => {
    setModalState({
      isOpen: false,
      mode: 'add',
      matrix: null,
      formData: { name: '', positionId: '', roleIds: [], templateToCopyId: null }
    });
    setSelectedPositionForRoles(null);
    setSelectedFunctionalRoles([]);
    setSelectedTechnicalRoles([]);
    setSelectedOrganizationalRoles([]);
    setCurrentStep(1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleNext = () => {
    if (currentStep === 1 && !modalState.formData.name.trim()) {
      showAlert('Please enter a matrix name', 'error');
      return;
    }
    const maxSteps = (modalState.mode === 'add' && showCopyTemplate) ? 3 : 2;
    if (currentStep < maxSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = async () => {
    const maxSteps = (modalState.mode === 'add' && showCopyTemplate) ? 3 : 2;
    if (currentStep < maxSteps) {
      handleNext();
      return;
    }

    if (!selectedPositionForRoles) {
      showAlert('Please select a position', 'error');
      return;
    }

    const allRoleIds = [
      ...selectedFunctionalRoles,
      ...selectedTechnicalRoles,
      ...selectedOrganizationalRoles
    ];

    const submitData = {
      name: modalState.formData.name,
      positionId: selectedPositionForRoles.id,
      roleIds: allRoleIds,
      ...(showCopyTemplate && modalState.formData.templateToCopyId && {
        templateToCopyId: modalState.formData.templateToCopyId
      }),
      ...additionalApiParams
    };

    try {
      setIsLoading(true);
      if (modalState.mode === 'add') {
        await skillsApi.createSkillsMatrix(submitData);
        showAlert('Skills matrix created successfully', 'success');
      } else {
        await skillsApi.updateSkillsMatrix(modalState.matrix.id, submitData);
        showAlert('Skills matrix updated successfully', 'success');
      }
      await fetchMatrices();
      handleModalClose();
    } catch (err) {
      showAlert(err.message || `Failed to ${modalState.mode} skills matrix. Please try again.`, 'error', err);
      console.error(`Error ${modalState.mode}ing skills matrix:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Box sx={{ p: 2 }}>
            <TextField
              label="Name"
              name="name"
              required
              fullWidth
              variant="outlined"
              placeholder="Enter matrix template name"
              value={modalState.formData.name}
              onChange={(e) => setModalState(prev => ({
                ...prev,
                formData: { ...prev.formData, name: e.target.value }
              }))}
              autoComplete="off"
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '56px'
                }
              }}
            />
          </Box>
        );
      case 2:
        return (
          <RolesSelection
            selectedPositionForRoles={selectedPositionForRoles}
            setSelectedPositionForRoles={setSelectedPositionForRoles}
            selectedFunctionalRoles={selectedFunctionalRoles}
            setSelectedFunctionalRoles={setSelectedFunctionalRoles}
            selectedTechnicalRoles={selectedTechnicalRoles}
            setSelectedTechnicalRoles={setSelectedTechnicalRoles}
            selectedOrganizationalRoles={selectedOrganizationalRoles}
            setSelectedOrganizationalRoles={setSelectedOrganizationalRoles}
            showOrganizationalRoles={false}
            initialPosition={selectedPositionForRoles}
            allowEmptyRolesSelection={true}
            hidePositionInfo={true}
          />
        );
      case 3:
        if (!showCopyTemplate) return null;
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Select a template to copy from (optional)
            </Typography>
            {matrices.length === 0 ? (
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: COLORS.GRAY }}>
                No templates available to copy from
              </Typography>
            ) : (
              <Box sx={{ 
                maxHeight: '400px', 
                overflowY: 'auto',
                pb: 2,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#a8a8a8',
                  },
                }
              }}>
                <MatrixGrid>
                  {matrices.map((matrix) => {
                    const isSelected = modalState.formData.templateToCopyId === matrix.id;
                    return (
                      <MatrixCard 
                        key={matrix.id}
                        onClick={() => setModalState(prev => ({
                          ...prev,
                          formData: { 
                            ...prev.formData, 
                            templateToCopyId: isSelected ? null : matrix.id 
                          }
                        }))}
                        style={{
                          cursor: 'pointer',
                          border: isSelected ? `1px solid rgb(220, 225, 235)` : '1px solid #E5E7EB',
                          borderRadius: '4px',
                          backgroundColor: '#f8f9fa',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.2s ease',
                          boxShadow: isSelected ? '0 4px 8px rgba(0, 0, 0, 0.1)' : 'none'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = addButtonColor;
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = '#E5E7EB';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {isSelected && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '5px',
                            backgroundColor: addButtonColor,
                          }} />
                        )}
                        <MatrixCardContent style={{
                          backgroundColor: isSelected ? 'rgba(168, 182, 212, 0.1)' : '#f8f9fa',
                        }}>
                          <MatrixTitle>
                            {matrix.name}
                            <div style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '16px',
                              backgroundColor: '#E3F2FD',
                              color: '#2196F3',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}>
                              {matrix.position?.label}
                            </div>
                          </MatrixTitle>
                          <MatrixDetailsGrid>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Roles</strong>
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {matrix.matrixRoles?.length > 0 ? (
                                matrix.matrixRoles.map(mr => {
                                  const { bgColor, textColor } = getRoleChipStyle(mr.role.type);
                                  return (
                                    <div
                                      key={mr.id}
                                      style={{
                                        padding: '6px 12px',
                                        borderRadius: '16px',
                                        backgroundColor: bgColor,
                                        fontSize: '13px',
                                        color: textColor,
                                        fontWeight: '500',
                                        border: `1px solid ${textColor}20`,
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                      }}
                                    >
                                      {mr.role.label}
                                    </div>
                                  );
                                })
                              ) : (
                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: COLORS.GRAY }}>
                                  No roles assigned
                                </Typography>
                              )}
                            </Box>
                          </MatrixDetailsGrid>
                        </MatrixCardContent>
                      </MatrixCard>
                    );
                  })}
                </MatrixGrid>
              </Box>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  const handleDeleteMatrix = (matrix) => {
    if (!allowDelete) return;
    setDeleteModal({ open: true, matrix });
  };

  const confirmDelete = async () => {
    const matrixToDelete = deleteModal.matrix;
    setDeleteModal({ open: false, matrix: null });
    
    if (!matrixToDelete?.id) {
      showAlert('Invalid matrix data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await skillsApi.deleteSkillsMatrix(matrixToDelete.id);
      await fetchMatrices();
      showAlert('Skills matrix deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting skills matrix:', err);
      showAlert(err.message || 'Failed to delete skills matrix.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredMatrices = matrices.filter(matrix => 
    matrix.name.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    matrix.position?.label.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const defaultEmptyTitle = allowAdd ? 
    "No matrix templates found" : 
    "No matrices found";
  
  const defaultEmptyDescription = searchTerm ? 
    "No matrices match your search criteria. Try adjusting your search term." : 
    allowAdd ? 
      `No matrix templates have been created yet. Click the '+ ${addButtonText}' button to create your first template.` :
      "No matrices are available for this session.";

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(backButtonPath)}>
            <FaArrowLeft /> {backButtonText}
          </BackButton>
          <Title>{title}</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton onClick={() => setShowControls(!showControls)}>
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <TextField
              placeholder={searchPlaceholder}
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch style={{ color: '#6B7280' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: '500px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  height: '40px',
                  fontSize: '14px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: addButtonColor,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: addButtonColor,
                  }
                },
                '& .MuiInputBase-input': {
                  padding: '8px 14px',
                }
              }}
            />

            {allowAdd && (
              <HeaderButton 
                onClick={() => handleModalOpen('add')}
                sx={{
                  minWidth: 'auto',
                  height: '32px',
                  padding: '0 16px',
                  backgroundColor: addButtonColor,
                  color: 'white',
                  alignSelf: 'flex-start',
                  '&:hover': {
                    backgroundColor: addButtonColor + 'dd',
                  }
                }}
              >
                <FaPlus style={{ marginRight: '8px' }} /> {addButtonText}
              </HeaderButton>
            )}
          </Box>
        </ControlsContainer>

        <MainContainer show={showControls}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div style={{ 
                color: '#64748B', 
                fontSize: '14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span style={{ fontWeight: 600 }}>
                  {filteredMatrices.length}
                </span>
                {filteredMatrices.length === 1 ? (allowAdd ? 'template' : 'matrix') : (allowAdd ? 'templates' : 'matrices')} found
              </div>
              {filteredMatrices.length === 0 ? (
                <EmptyState
                  title={emptyStateTitle || defaultEmptyTitle}
                  description={emptyStateDescription || defaultEmptyDescription}
                />
              ) : (
                <MatrixGrid>
                  {filteredMatrices.map((matrix) => (
                    <MatrixCard key={matrix.id}>
                      <MatrixCardContent>
                        <MatrixTitle>
                          {matrix.name}
                          <div style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            backgroundColor: '#E3F2FD',
                            color: '#2196F3',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            {matrix.position?.label}
                          </div>
                        </MatrixTitle>
                        <MatrixDetailsGrid>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Roles</strong>
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {matrix.matrixRoles?.length > 0 ? (
                              matrix.matrixRoles.map(mr => {
                                const { bgColor, textColor } = getRoleChipStyle(mr.role.type);
                                
                                return (
                                  <div
                                    key={mr.id}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: '16px',
                                      backgroundColor: bgColor,
                                      fontSize: '13px',
                                      color: textColor,
                                      fontWeight: '500',
                                      border: `1px solid ${textColor}20`,
                                      display: 'inline-flex',
                                      alignItems: 'center'
                                    }}
                                  >
                                    {mr.role.label}
                                  </div>
                                );
                              })
                            ) : (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: COLORS.GRAY }}>
                                No roles assigned
                              </Typography>
                            )}
                          </Box>
                          {matrix.templates && matrix.templates.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                              <Typography variant="body2" sx={{ mb: 0 }}>
                                <strong>Versions</strong>
                              </Typography>
                              {Object.values(MATRIX_TEMPLATE_TYPES).map(type => {
                                const typeVersions = matrix.templates.filter(template => template.type === type);
                                if (typeVersions.length === 0) return null;

                                return (
                                  <Box key={type} sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    gap: 2
                                  }}>
                                    <Typography variant="caption" sx={{ 
                                      color: getTemplateTypeColor(type),
                                      fontWeight: 500,
                                      fontSize: '12px',
                                      minWidth: '140px'
                                    }}>
                                      {getTemplateTypeText(type)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                      {typeVersions.map(template => (
                                        <Chip
                                          key={template.id}
                                          label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                              <span>{`v. ${template.version}`}</span>
                                              {template._count?.skillsTemplates > 0 ? (
                                                <>
                                                  <FaCircle style={{ fontSize: '6px', opacity: 0.6 }} />
                                                  <span>{template._count.skillsTemplates}</span>
                                                </>
                                              ) : (
                                                <>
                                                  <FaExclamationCircle style={{ 
                                                    fontSize: '10px', 
                                                    color: '#d97706' 
                                                  }} />
                                                  <span style={{ 
                                                    color: '#d97706',
                                                    fontWeight: 600
                                                  }}>
                                                    0
                                                  </span>
                                                </>
                                              )}
                                            </Box>
                                          }
                                          size="small"
                                          sx={{ 
                                            backgroundColor: getStatusColor(template.publishedAt ? MATRIX_TEMPLATE_STATUS.PUBLISHED : MATRIX_TEMPLATE_STATUS.DRAFT),
                                            color: getStatusTextColor(template.publishedAt ? MATRIX_TEMPLATE_STATUS.PUBLISHED : MATRIX_TEMPLATE_STATUS.DRAFT),
                                            border: '1px solid',
                                            borderColor: `${getStatusTextColor(template.publishedAt ? MATRIX_TEMPLATE_STATUS.PUBLISHED : MATRIX_TEMPLATE_STATUS.DRAFT)}20`,
                                            height: '24px',
                                            '& .MuiChip-label': {
                                              fontSize: '13px',
                                              padding: '0 8px',
                                              fontWeight: 500,
                                              display: 'flex',
                                              alignItems: 'center'
                                            }
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Versions</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: COLORS.GRAY }}>
                                {noVersionsMessage}
                              </Typography>
                            </Box>
                          )}
                        </MatrixDetailsGrid>
                        {(allowEdit || allowDelete || allowManageVersions || allowSelectVersions) && (
                          <ActionButtons>
                            {allowEdit && (
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleModalOpen('edit', matrix)}
                              >
                                <FaEdit />
                              </IconButton>
                            )}
                            {allowDelete && (
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteMatrix(matrix)}
                                color="error"
                              >
                                <FaTrash />
                              </IconButton>
                            )}
                            {allowSelectVersions && sessionId && (
                              <IconButton
                                onClick={() => navigate(`/skills-matrix/sessions/${sessionId}/matrices/${matrix.id}/select-versions`)}
                                color="primary"
                                size="small"
                                title="Select Versions"
                              >
                                <FaCog />
                              </IconButton>
                            )}
                            {allowManageVersions && (
                              <IconButton
                                onClick={() => navigate(`/skills-matrix/templates/${matrix.id}/versions`)}
                                color="primary"
                                size="small"
                                title="Manage Versions"
                              >
                                <FaGripHorizontal />
                              </IconButton>
                            )}
                          </ActionButtons>
                        )}
                      </MatrixCardContent>
                    </MatrixCard>
                  ))}
                </MatrixGrid>
              )}
            </>
          )}
        </MainContainer>
      </Content>

      {/* Matrix Template Modal */}
      {allowCRUD && (
        <Modal
          isOpen={modalState.isOpen}
          onClose={handleModalClose}
          title={modalState.mode === 'add' ? 'Add New Matrix Template' : 'Edit Matrix Template'}
          onSubmit={handleSubmit}
          submitText={currentStep < getSteps(modalState.mode, showCopyTemplate).length ? 'Next' : (modalState.mode === 'add' ? 'Create Template' : 'Save Changes')}
          cancelText={currentStep === 1 ? 'Cancel' : 'Back'}
          onCancel={currentStep === 1 ? handleModalClose : handleBack}
          submitDisabled={currentStep === 1 ? !modalState.formData.name.trim() : !selectedPositionForRoles}
          maxWidth={currentStep === 1 ? 'md' : 'lg'}
        >
          <Box sx={{ width: '100%', mb: 4 }}>
            <Stepper activeStep={currentStep - 1}>
              {getSteps(modalState.mode, showCopyTemplate).map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          {getStepContent()}
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {allowDelete && (
        <ConfirmationModal
          isOpen={deleteModal.open}
          onClose={() => setDeleteModal({ open: false, matrix: null })}
          onConfirm={confirmDelete}
          title="Delete Matrix Template"
          message={`Are you sure you want to delete the matrix template "${deleteModal.matrix?.name}"? This action cannot be undone.`}
        />
      )}
    </Container>
  );
};

export default SkillsMatrixList; 